import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import cmsPageObject from '@/entities/cmsPage/interface'

import {
  ALL_CATEGORIES_QUERY,
  SINGLE_CATEGORY_QUERY,
  CATEGORIES_DEPENDENTS_QUERY,
  CREATE_CATEGORY_MUTATION,
  UPDATE_CATEGORY_MUTATION,
  DELETE_CATEGORY_MUTATION,
  FORCE_DELETE_CATEGORY_MUTATION,
  ADD_CATEGORY_PAGES_MUTATION,
  REMOVE_CATEGORY_PAGES_MUTATION,
  CREATE_CATEGORY_PAGES_MUTATION,
  DELETE_CATEGORY_PAGES_MUTATION,
  UPDATE_CATEGORY_PAGES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsCategoryRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_CATEGORY_QUERY, variables).then((data: any) => {
      return hydrateObject(data.category)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_CATEGORIES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.categories)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_CATEGORY_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.category),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_CATEGORIES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.categories),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(CATEGORIES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([CATEGORIES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_CATEGORY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.category)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_CATEGORY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.category)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_CATEGORY_MUTATION, variables).then((data: any) => {
      return data.deleteCmsCategory
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_CATEGORY_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsCategory
    })
  },

  assignPages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pageIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_CATEGORY_PAGES_MUTATION, variables)
  },

  unassignPages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pageIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_CATEGORY_PAGES_MUTATION, variables)
  },

  createPages: async (id: string, items: Array<cmsPageObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pages: items,
      },
    }
    return graphQLClient.request(CREATE_CATEGORY_PAGES_MUTATION, variables)
  },

  deletePages: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pageIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_CATEGORY_PAGES_MUTATION, variables)
  },

  updatePagesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        pageIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_CATEGORY_PAGES_ORDER_MUTATION, variables)
  },
}

export default cmsCategoryRepository
